import { graphql } from 'react-relay';

import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

type useCreateAgentMenuItemQueryData = {
	readonly atlassianStudio_userSiteContext?: {
		readonly userPermissions?: {
			readonly isAbleToCreateAgents: boolean | null;
		} | null;
	} | null;
};

type useCreateAgentMenuItemResult = {
	isEligible: boolean;
	loading: boolean;
	createAgentUrl: string;
};

export const useCreateAgentMenuItem = (): useCreateAgentMenuItemResult => {
	const { cloudId, isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const skip =
		!cloudId ||
		!isRovoLLMEnabled ||
		isAnonymousUser ||
		!fg('cc_fd_agent_creation_connie_create_gate');

	const { data, loading } = useQueryInterop<useCreateAgentMenuItemQueryData>(
		graphql`
			query useCreateAgentMenuItemStudioPermissionsQuery($cloudId: ID!) {
				atlassianStudio_userSiteContext(cloudId: $cloudId) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							isAbleToCreateAgents
						}
					}
				}
			}
		`,
		{
			variables: { cloudId },
			skip,
		},
	);

	const hasPermission =
		data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents ?? false;

	const createAgentUrl = getAtlassianStudioNLCreateUrl(cloudId, 'connie-create');

	return {
		isEligible: !skip && hasPermission,
		loading: !skip ? loading : false,
		createAgentUrl,
	};
};
